import { removeDupes, cleanObject } from 'utils/helpers';
import { Goal, FetchState } from 'types';

interface StateType {
    items: Goal[],
    fetchInfo: {
        team: FetchState
    },
    shouldRefetch: boolean
}

interface Payload {
    [key: string]: any
}

interface ActionType {
    error: boolean,
    type: string,
    payload: Payload
}

const DEFAULT_STATE: StateType = {
    items: [],
    fetchInfo: {
        team: {}
    },
    shouldRefetch: false
}

const goals = (state = { ...DEFAULT_STATE }, action: ActionType) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
        case 'RESET_STORE':
            return { ...DEFAULT_STATE }

        case 'MARK_ALL_FOR_REFETCH':
            return Object.assign({}, state, {
                shouldRefetch: true
            });

        case 'GOALS_FETCH_REQUESTED':
            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        ...state.fetchInfo.team,
                        loading: true,
                        error: false
                    }
                }
            });

        case 'GOALS_FETCH_SUCCEEDED':
            if (!action.payload.items) {
                return state;
            }

            return Object.assign({}, state, {
                items: removeDupes([
                    ...action.payload.items,
                    ...state.items
                ]),
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        ...state.fetchInfo.team,
                        loading: false,
                        fetched: true,
                        error: false
                    }
                }
            });

        case 'GOALS_FETCH_FAILED':
            return Object.assign({}, state, {
                items: removeDupes([
                    ...action.payload.items,
                    ...state.items
                ]),
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        ...state.fetchInfo.team,
                        loading: false,
                        fetched: true,
                        error: true
                    }
                }
            });

        case 'GOAL_CREATE_REQUESTED':
        case 'GOAL_ADD_SOCKET':
            return Object.assign({}, state, {
                items: removeDupes([...state.items, action.payload.goal])
            });

        case 'GOAL_CREATE_FAILED':
            return Object.assign({}, state, {
                items: state.items.filter(goal => goal !== action.payload.fragment)
            });

        case 'GOAL_CREATE_SUCCEEDED':
            return Object.assign({}, state, {
                items: state.items
                    .filter(goal => goal.id !== action.payload.goalId)
                    .map(goal => {
                        if (goal === action.payload.fragment) {
                            goal.id = action.payload.goalId;
                            goal.progress = action.payload.progress;
                            goal.source = action.payload.source;
                        }

                        return goal;
                    })
            });

        case 'GOAL_FETCH_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items.filter(goal => goal.id !== action.payload.goalId), {
                    ...(state.items.find(goal => goal.id === action.payload.goalId) || {}),
                    id: action.payload.goalId,
                    loading: true
                }]
            });

        case 'GOAL_FETCH_FAILED':
            return Object.assign({}, state, {
                items: [...state.items.filter(goal => goal.id !== action.payload.goalId), {
                    id: action.payload.goalId,
                    removed: true
                }]
            });

        case 'GOAL_FETCH_SUCCEEDED':
            if (!action.payload?.goal?.id) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.filter(goal => goal.id !== action.payload.goal.id), action.payload.goal]
            });

        case 'GOAL_UPDATE_REQUESTED':
        case 'GOAL_UPDATE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(goal => {
                    if (goal.id === action.payload.goalId) {
                        return Object.assign({}, goal, cleanObject(action.payload.goalProps || action.payload.goal));
                    }

                    return goal;
                })
            });

        case 'GOALS_UPDATE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(goal => {
                    const updatedProps = action.payload.goals.find((g: Partial<Goal>) => g.id === goal.id);

                    if (updatedProps) {
                        return Object.assign({}, goal, cleanObject(updatedProps));
                    }

                    return goal;
                })
            });

        case 'GOAL_ASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(goal => {
                    if (goal.id === action.payload.goalId) {
                        return Object.assign({}, goal, {
                            members: [...(goal.members || []), ...action.payload.users].filter((value, index, array) => array.indexOf(value) === index)
                        });
                    }

                    return goal;
                })
            });

        case 'GOAL_UNASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(goal => {
                    if (goal.id === action.payload.goalId) {
                        return Object.assign({}, goal, {
                            members: goal.members.filter(userId => !action.payload.users.includes(userId))
                        });
                    }

                    return goal;
                })
            });

        case 'GOAL_REMOVE_REQUESTED':
        case 'GOAL_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(goal => {
                    if (goal.id === action.payload.goalId) {
                        return {
                            id: goal.id,
                            removed: true
                        };
                    }

                    return goal;
                })
            });

        default:
            return state;
    }
}

export default goals;
